import React from 'react';
import { App as CapApp } from '@capacitor/app';
import { isNativeApp } from '../utils/appLinks';
import {
  appMetadata,
  formatBuildVersion,
  getBuildVersion,
} from '../utils/buildVersion';

interface BuildVersionTagProps {
  className?: string;
}

export default function BuildVersionTag({ className = '' }: BuildVersionTagProps) {
  const [nativeVersion, setNativeVersion] = React.useState<{ version: string; build: string } | null>(null);
  const rawBuildVersion = appMetadata.buildVersion || getBuildVersion();

  React.useEffect(() => {
    if (!isNativeApp()) return;
    let disposed = false;
    void CapApp.getInfo()
      .then((info) => {
        if (disposed) return;
        const version = (info.version || '').trim();
        if (!version) return;
        setNativeVersion({
          version,
          build: (info.build || '').trim(),
        });
      })
      .catch(() => {
        // ignore - fallback to build metadata
      });
    return () => {
      disposed = true;
    };
  }, []);

  const buildVersion = React.useMemo(() => {
    const webFormatted = formatBuildVersion(rawBuildVersion);
    if (nativeVersion?.version) {
      const rawNativeBuild = (nativeVersion.build || '').trim();
      if (rawNativeBuild) {
        const normalizedNativeBuild = rawNativeBuild.replace(/^t/i, '');
        return `${webFormatted} (${normalizedNativeBuild})`;
      }
      return webFormatted;
    }
    return webFormatted;
  }, [nativeVersion, rawBuildVersion]);

  if (!buildVersion) {
    return null;
  }

  return (
    <span
      className={`block text-[10px] leading-tight text-(--color-text)/60 ${className}`}
      title={`Build ${buildVersion}`}
      aria-label={`Build ${buildVersion}`}
      data-testid="build-version-tag"
    >
      Build {buildVersion}
    </span>
  );
}
